import React from "react";
import styled from "styled-components";
import Button from "components/atoms/Button";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import { UserManualManager } from "components/organisms/CommonUI";
import { COLOR_SYSTEM } from "components/design/design-system";
import { eventLog } from "utils/AmplitudeUtil";

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  padding: 40px 20px;
`;

const EmptyStateCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding: 48px;
  max-width: 600px;
  width: 100%;
`;


const StepsContainer = styled.div`
  margin: 32px 32px 24px 32px;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const StepsTitle = styled.div`
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  margin-bottom: 16px;
  text-align: center;
  font-size: 14px;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const StepNumber = styled.span`
  background: ${COLOR_SYSTEM.get("Blue")?.[500]};
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: center;
`;

const TipBox = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 12px;
  padding: 20px;
  margin-top: 32px;
  text-align: left;
`;

interface DashboardEmptyStateProps {
  onAddPatient: () => void;
}

export const DashboardEmptyState: React.FC<DashboardEmptyStateProps> = ({ onAddPatient }) => {
  const handleUserGuideClick = () => {
    eventLog("EmptyState 사용법 보기");
    // UserManualManager API로 팝업 열기
    UserManualManager.show({
      shouldUpdateVersion: false,  // 수동 팝업: DB 업데이트 안 함
    });
  };

  return (
    <EmptyStateContainer>
      <EmptyStateCard>


        <Text
          element="h1"
          textType="H1"
          color={COLOR_SYSTEM.get("Gray")?.[800]}
          style={{ marginBottom: "16px" }}
        >
          😊 환영합니다, 약사님!<br/>
        </Text>
        
        
        <Text
          element="p"
          textType="Body1"
          color={COLOR_SYSTEM.get("Gray")?.[600]}
          style={{ marginBottom: "24px", lineHeight: "1.6" }}
        >
          처음 2번은 무료예요. 부담 없이 돌려보세요. <br/>오늘 만난 환자 중 이런 환자 — 약 많았던 환자, 처방전 여러 장, 모르는 약, 기저질환 많은 환자.
        </Text>

        <StepsContainer>
          <StepsTitle>
            [Quick 사용 가이드]
          </StepsTitle>
          <StepList>
            <StepItem>
              <StepNumber>1</StepNumber>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ fontWeight: 500 }}>
                <strong>새환자등록:</strong> 나이/성별 입력
              </Text>
            </StepItem>
            <StepItem>
              <StepNumber>2</StepNumber>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ fontWeight: 500 }}>
                <strong>6가지 환자정보입력:<br/> </strong> 질병정보, 약물 부작용 및 알레르기정보, 추가정보, <br/>처방의약품, 일반의약품, 건강기능식품
              </Text>
            </StepItem>
            <StepItem>
              <StepNumber>3</StepNumber>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ fontWeight: 500 }}>
                <strong>AI 검토 시작</strong>
              </Text>
            </StepItem>
          </StepList>
        </StepsContainer>

        <Text 
          textType="Caption" 
          color={COLOR_SYSTEM.get("Gray")?.[500]} 
          style={{ marginTop: "16px", marginBottom: "8px", textAlign: "center", fontStyle: "italic" }}
        >
          처음 사용하시는 경우 자세한 사용방법을 확인 후 사용하세요
        </Text>

        <ButtonContainer>
          <Button
            size="Large"
            type="IconWithText"
            variant="Contained"
            color="Primary"
            icon="Question Mark"
            left
            onClick={handleUserGuideClick}
          >
            더 자세한 사용법 보기
          </Button>
          
          <Button
            size="Large"
            type="IconWithText"
            variant="Outlined"
            color="Primary"
            icon="Plus"
            left
            onClick={onAddPatient}
          >
            첫 환자 등록하기
          </Button>
        </ButtonContainer>

      </EmptyStateCard>
    </EmptyStateContainer>
  );
};

export default DashboardEmptyState;
